import { useState, useCallback, useRef } from 'react';
import { devLog, devWarn, devError } from '../utils/devLogger';
import { likeLot } from '../lib/api/user/userApi';

export interface FavoriteUpdateEvent {
  lotId: number;
  isLiked: boolean;
  likedUserIndexes: number[];
}

export interface FavoriteManager {
  handleLikeClick: (lotId: number, currentUserIndex?: number, currentLikedUserIndexes?: number[] | null) => Promise<void>;
  onFavoriteUpdate: (callback: (event: FavoriteUpdateEvent) => void) => () => void;
  isProcessing: boolean;
}

export const useFavoriteManager = (): FavoriteManager => {
  const [isProcessing, setIsProcessing] = useState(false);
  const updateCallbacksRef = useRef<((event: FavoriteUpdateEvent) => void)[]>([]);
  const pendingLotIdsRef = useRef(new Set<number>());

  // 좋아요 상태 업데이트 콜백 등록
  const onFavoriteUpdate = useCallback((callback: (event: FavoriteUpdateEvent) => void) => {
    updateCallbacksRef.current.push(callback);
    
    // cleanup 함수 반환
    return () => {
      updateCallbacksRef.current = updateCallbacksRef.current.filter(cb => cb !== callback);
    };
  }, []);

  // 좋아요 처리 함수
  const handleLikeClick = useCallback(async (
    lotId: number, 
    currentUserIndex?: number, 
    currentLikedUserIndexes?: number[] | null
  ) => {
    if (pendingLotIdsRef.current.has(lotId)) return;

    pendingLotIdsRef.current.add(lotId);
    setIsProcessing(true);

    const likedUserIndexes = currentLikedUserIndexes ?? [];
    let newLikedUserIndexes = likedUserIndexes;
    let isLiked = false;
    
    if (currentUserIndex !== undefined) {
      if (likedUserIndexes.includes(currentUserIndex)) {
        newLikedUserIndexes = likedUserIndexes.filter(index => index !== currentUserIndex);
      } else {
        newLikedUserIndexes = [...likedUserIndexes, currentUserIndex];
        isLiked = true;
      }
    }

    const updateEvent: FavoriteUpdateEvent = {
      lotId,
      isLiked,
      likedUserIndexes: newLikedUserIndexes
    };
    updateCallbacksRef.current.forEach((callback: (event: FavoriteUpdateEvent) => void) => callback(updateEvent));

    try {
      await likeLot(lotId);
    } catch (error) {
      devError('좋아요 처리 실패:', error);
      updateCallbacksRef.current.forEach((callback: (event: FavoriteUpdateEvent) => void) => callback({
        lotId,
        isLiked: !isLiked,
        likedUserIndexes
      }));
      throw error;
    } finally {
      pendingLotIdsRef.current.delete(lotId);
      setIsProcessing(pendingLotIdsRef.current.size > 0);
    }
  }, []);

  return {
    handleLikeClick,
    onFavoriteUpdate,
    isProcessing
  };
};
